import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Redirect } from 'src/routes/Redirect'
import { Meta } from 'src/system/Meta'
import { useRouting } from 'src/routes'
import { Avatar } from 'src/system/ui/image/Avatar'
import {
  useProfile,
  usePublicProfile,
  usePublicProfileSteam
} from 'src/hooks/use-profile'
import { AppBar } from 'src/system/app-bar/AppBar'
import { PublicProgressionReport } from 'src/screens/progression-report/ProgressionReportProfile'
import { Background } from 'src/system/ui/layout/Background'
import { CenterLoadingSpinner } from 'src/system/ui/LoadingSpinner'
import { PageTitle } from 'src/system/ui/typography/PageTitle'

export function PublicProfile() {
  const { profileId } = useParams<'profileId'>()

  if (profileId == null) {
    return null
  }

  return (
    <>
      <AppBar />
      <Meta name='profile' />
      <Background>
        <Profile profileId={profileId} />
      </Background>
    </>
  )
}

function Profile(props: { profileId: string }) {
  const { toProfile } = useRouting()
  const { profileId } = props

  // Identity only — the steam profile and the progression report are fetched
  // alongside this by the children, so neither one gates the page.
  const { data, loading } = usePublicProfile(profileId)

  if (!data && loading) return <ProfileLoading />
  const id = data?.profile?.id

  if (id != null && id !== profileId) {
    return <Redirect to={toProfile(id)} />
  }

  if (!loading && !data?.profile) return <ProfileNotFoundError />

  return (
    <Container>
      <ProfileInfo profileId={profileId} />
      <PublicProgressionReport profileId={profileId} />
    </Container>
  )
}

function ProfileInfo(props: { profileId: string }) {
  const { data, loading } = usePublicProfileSteam(props.profileId)
  const { t } = useTranslation()
  const cachedSteam = useOwnSteamProfile(props.profileId)

  const steamProfile = data?.profile?.steam ?? cachedSteam
  // Steam comes back from a live Steam Web API call, so it can lag the rest of
  // the page. Until it lands we title the page with the profile id.
  const isPending = steamProfile == null && loading

  const name = steamProfile?.name ?? props.profileId
  const image = steamProfile?.avatarUrl
  const titleText = t('overview:profile:intro', {
    name
  })
  return (
    <TitleContainer>
      <Meta name='profile' image={image} titleVars={{ name }} />
      <InfoBox>
        <StyledAvatar src={image} pulse={isPending} />
        <StyledPageTitle pulse={isPending}>{titleText}</StyledPageTitle>
      </InfoBox>
    </TitleContainer>
  )
}

// The app bar already keeps the signed-in user's steam profile in the Apollo
// cache, so your own page can paint its avatar and name without waiting. Read
// it cache-only: guests and other players' pages never fire a request for it.
function useOwnSteamProfile(profileId: string) {
  const { profile } = useProfile('cache-only')
  return profile?.id === profileId ? profile.steam : undefined
}

// Only the small identity query gates this now, so it's a brief spinner rather
// than the long ramp the whole-page load used to need. The slow parts announce
// themselves in place: the steam header pulses, the report has its own loading
// copy.
function ProfileLoading() {
  const { t } = useTranslation()
  return (
    <LoadingBox>
      <LoadingText>{t('overview:profile:loading')}</LoadingText>
      <CenterLoadingSpinner />
    </LoadingBox>
  )
}

function ProfileNotFoundError() {
  const { t } = useTranslation()

  return (
    <>
      <NotFoundErrorText>
        {t('overview:profile:not_found_error')}
      </NotFoundErrorText>
    </>
  )
}

const LoadingBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.theme.d.spacing[6]};
  padding: ${(props) => props.theme.d.spacing[20]} 0;
`

const LoadingText = styled.p`
  color: ${(props) => props.theme.d.colors.text.primary};
  font-size: 18px;
  line-height: 150%;
  text-align: center;
  max-width: 420px;
  margin: 0;
`

const NotFoundErrorText = styled.div`
  color: ${(props) => props.theme.d.colors.error};
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`

const InfoBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const placeholderPulse = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.6; }
`

const pulseWhilePending = (props: { pulse?: boolean }) =>
  props.pulse
    ? css`
        animation: ${placeholderPulse} 1.4s ease-in-out infinite;

        @media (prefers-reduced-motion: reduce) {
          animation: none;
          opacity: 0.5;
        }
      `
    : ''

const StyledAvatar = styled(Avatar)<{ pulse?: boolean }>`
  display: block;
  height: 64px;
  width: 64px;
  margin-right: 15px;

  ${pulseWhilePending}
`

const TitleContainer = styled.div`
  max-width: 1256px;
  margin-left: auto;
  margin-right: auto;
  margin-top: ${(props) => props.theme.d.spacing[10]};
`

const StyledPageTitle = styled(PageTitle)<{ pulse?: boolean }>`
  margin-bottom: 0;

  ${pulseWhilePending}
`

const Container = styled.div`
  flex: 1;
  flex-direction: column;
  max-width: 1256px;
  margin-left: auto;
  margin-right: auto;
`
